import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { REASONCONFIG } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const PredefinedMessageForm = NiceModal.create(({ id, modalName, mode }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					title: yup.string().nullable().required(),
					message: yup.string().nullable().required()
				})
				.required(),
		[]
	);
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			title: null,
			message: null
		}
	});

	const {
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const fillFormInEdit = async (id) => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(REASONCONFIG.GET_PREDEFINED_MESSAGE, {
				ID: id
			});
			const { data } = response;

			reset({
				ID: data.ID ?? null,
				title: data.title ?? null,
				message: data.message ?? null
			});
		} catch (error) {
			AppLogger.error(error);
			showError();
		}

		setIsLoading(false);
	};

	useEffect(() => {
		if (id) fillFormInEdit(id);
	}, [id]);

	const onSubmit = async (data) => {
		// row means we are in edit mode else is add new branch.

		setIsLoading(true);
		const URL = id ? REASONCONFIG.UPDATE_PREDEFINED_MESSAGE : REASONCONFIG.ADD_PREDEFINED_MESSAGE;

		try {
			const response = await axiosPrivate.post(URL, {
				ID: id,
				...data
			});
			const { status } = await response;
			if (status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.PREDEFINED_MESSAGES));
				showSuccess();
				handleCloseModal();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
	};

	return (
		<Modal fullwidth maxWidth="small" name={modalName} className="modal-reasonConfig">
			<ModalTitle>
				<Typography variant="p1">predefinedMessageForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<Alert content="predefinedMessages.description" className="m-4" showIcon />

					<FormItem label="title">
						<StringInput disabled={mode === 'view'} required name={`title`} />
					</FormItem>
					<FormItem label="message">
						<TextArea name="message" placeholder="" disabled={mode === 'view'} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={mode === 'view' ? 'close' : 'cancel'} variant="outline" onClick={handleCloseModal} />
				{mode !== 'view' ? (
					<Button
						loading={isLoading}
						label={id ? 'save' : 'add'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						iconPos="right"
					/>
				) : null}
			</ModalActions>
		</Modal>
	);
});
export default PredefinedMessageForm;
